import React, { useRef, useState, useEffect } from 'react';
import { X, RotateCcw, Check, Pen } from 'lucide-react';

const DigitalSignature = ({ 
  onSignatureCapture, 
  onCancel, 
  signerName = '', 
  signatureType = 'technician',
  isOpen = false,
  disabled = false 
}) => {
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  const [comments, setComments] = useState('');
  const [deviceInfo, setDeviceInfo] = useState('');

  useEffect(() => {
    if (isOpen) {
      // Get device information
      const userAgent = navigator.userAgent;
      const platform = navigator.platform;
      setDeviceInfo(`${platform} - ${userAgent.substring(0, 100)}...`);
      
      // Initialize canvas
      const canvas = canvasRef.current;
      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.strokeStyle = '#000000';
        ctx.lineWidth = 2;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        
        // Set canvas size
        const rect = canvas.getBoundingClientRect();
        canvas.width = rect.width * window.devicePixelRatio;
        canvas.height = rect.height * window.devicePixelRatio;
        ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
        
        // Clear canvas with white background
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
      }
    }
  }, [isOpen]);

  const getCoordinates = (event) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    if (event.touches && event.touches.length > 0) {
      // Touch event
      return {
        x: event.touches[0].clientX - rect.left,
        y: event.touches[0].clientY - rect.top
      };
    } else {
      // Mouse event
      return {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top
      };
    }
  };

  const startDrawing = (event) => {
    if (disabled) return;
    
    event.preventDefault();
    setIsDrawing(true);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(event);
    
    ctx.beginPath();
    ctx.moveTo(coords.x, coords.y);
  };

  const draw = (event) => {
    if (!isDrawing || disabled) return;
    
    event.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const coords = getCoordinates(event);
    
    ctx.lineTo(coords.x, coords.y);
    ctx.stroke();
    
    setHasSignature(true);
  };

  const stopDrawing = (event) => {
    if (!isDrawing) return;
    
    event.preventDefault();
    setIsDrawing(false);
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    ctx.closePath();
  };

  const clearSignature = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Set white background
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    setHasSignature(false);
  };

  const captureSignature = () => {
    if (!hasSignature) {
      alert('Please provide a signature before confirming.');
      return;
    }

    const canvas = canvasRef.current;
    const signatureData = canvas.toDataURL('image/png');
    
    const signatureInfo = {
      signature_data: signatureData,
      signature_type: signatureType,
      comments: comments.trim(),
      device_info: deviceInfo,
      timestamp: new Date().toISOString()
    };
    
    onSignatureCapture(signatureInfo);
  };

  const getSignatureTypeLabel = () => {
    switch (signatureType) {
      case 'technician':
        return 'Technician Signature';
      case 'approval':
        return 'Approval Signature';
      case 'witness':
        return 'Witness Signature';
      default:
        return 'Digital Signature';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {getSignatureTypeLabel()}
            </h2>
            {signerName && (
              <p className="text-sm text-gray-600 mt-1">
                Signer: {signerName}
              </p>
            )}
          </div>
          <button
            onClick={onCancel}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            disabled={disabled}
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Signature Canvas */}
        <div className="p-6">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Please sign below:
            </label>
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 bg-gray-50">
              <canvas
                ref={canvasRef}
                className="w-full h-48 bg-white border border-gray-200 rounded cursor-crosshair"
                onMouseDown={startDrawing}
                onMouseMove={draw}
                onMouseUp={stopDrawing}
                onMouseLeave={stopDrawing}
                onTouchStart={startDrawing}
                onTouchMove={draw}
                onTouchEnd={stopDrawing}
                style={{ touchAction: 'none' }}
              />
            </div>
            <div className="flex items-center justify-between mt-2">
              <div className="flex items-center text-sm text-gray-500">
                <Pen className="h-4 w-4 mr-1" />
                Use mouse or touch to sign
              </div>
              <button
                onClick={clearSignature}
                className="flex items-center text-sm text-gray-600 hover:text-gray-800 transition-colors"
                disabled={disabled}
              >
                <RotateCcw className="h-4 w-4 mr-1" />
                Clear
              </button>
            </div>
          </div>

          {/* Comments Section */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e.target.value)}
              placeholder="Add any additional comments or notes..."
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              rows={3}
              disabled={disabled}
            />
          </div>

          {/* Device Info */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Device Information
            </label>
            <div className="text-xs text-gray-500 bg-gray-50 p-2 rounded border">
              {deviceInfo}
            </div>
          </div>

          {/* Legal Notice */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Legal Notice:</strong> By providing your digital signature, you acknowledge that:
            </p>
            <ul className="text-xs text-blue-700 mt-2 ml-4 list-disc">
              <li>This signature has the same legal effect as a handwritten signature</li>
              <li>You are authorized to sign this document</li>
              <li>The information provided is accurate and complete</li>
              <li>This signature will be cryptographically secured and timestamped</li>
            </ul>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
            disabled={disabled}
          >
            Cancel
          </button>
          <button
            onClick={captureSignature}
            disabled={!hasSignature || disabled}
            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Check className="h-4 w-4 mr-2" />
            Confirm Signature
          </button>
        </div>
      </div>
    </div>
  );
};

export default DigitalSignature;